import React, { useState, useRef, useEffect } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Send, Copy, Check } from 'lucide-react';
import { copyRich, htmlToPlain } from '../../lib/richClipboard';
import { chatService } from '../../services/chatService';
import { describeChatError } from './summarizeError';
import { protocolReadyMessage } from '../../lib/protocolHints';
import { changedLines } from '../../lib/lineDiff';
import { BTN_SECONDARY } from '../../lib/buttonStyles';

// Сколько строк diff показываем в реплике; хвост сворачивается в «ещё N строк».
const DIFF_LINES_SHOWN = 24;

/**
 * Правка доступна, когда открыт протокол, текст есть и родитель умеет его
 * принять. Сам режим человек выбирает переключателем «Вопрос / Правка»
 * (решение владельца 14.09.2026: по умолчанию — вопрос; классификатор
 * намерения, который выберет режим сам, — следующий шаг,
 * docs/backlog/chat-edits-protocol.md). В правке сообщение уходит как
 * mode=edit и возвращается с новой версией — человек видит diff и решает
 * «Применить / Не надо».
 */
export function editAvailable({ activeTab, protocolText, onProtocolChange }) {
  return activeTab === 'protocol' && !!(protocolText || '').trim() && typeof onProtocolChange === 'function';
}

export function editMode({ activeTab, protocolText, onProtocolChange, mode }) {
  return mode === 'edit' && editAvailable({ activeTab, protocolText, onProtocolChange });
}

export default function ChatPanel({ visible, onClose, transcriptionText, rawTranscriptionText, protocolText, activeTab, model, canShare, onShare, onUnread, onProtocolChange, meetingAt }) {
  // В чат отдаём версию С таймстампами, если она есть, — чтобы можно было
  // спрашивать «на какой минуте обсуждали…». Фолбэк — обычный текст
  // (например, при вручную вставленном тексте меток времени нет).
  const chatTranscription = rawTranscriptionText || transcriptionText;
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  // 'ask' | 'edit' — переключатель над полем ввода; по умолчанию вопрос.
  const [mode, setMode] = useState('ask');
  const [loading, setLoading] = useState(false);
  const bottomRef = useRef(null);
  const greetedRef = useRef(false);
  const nudgedRef = useRef(false);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Проактивное приветствие: при заходе с транскриптом и пустым чатом —
  // одна короткая живая реплика об этой встрече (discovery для чата).
  useEffect(() => {
    if (!visible || greetedRef.current) return;
    if (!transcriptionText || messages.length > 0) return;
    greetedRef.current = true;
    (async () => {
      setLoading(true);
      try {
        const instruction = 'Ты прочитал транскрипт встречи СО СТОРОНЫ (не был на ней). Живая короткая реплика ≤50 слов, с эмоцией и характером (без канцелярита), связным текстом: подметь самое цепляющее со стороны и сразу порекомендуй подходящий вид протокола (управленческое резюме / задачи / риски / решения — выбери по содержанию), а в конце вежливо подтолкни обсудить это со мной одним точным вопросом. Голос наблюдателя («по транскрипту видно…»), без «мы/поздравляю», без выдумок, без приветствий и без списков.';
        const resp = await chatService.send(instruction, { transcription: chatTranscription, meetingAt }, [], model);
        // не затираем чат, если юзер успел написать за время ответа
        setMessages((prev) => (prev.length ? prev : [{ role: 'assistant', content: resp }]));
      } catch {
        // одна попытка на заход; повтор — при следующем маунте (ref сбросится)
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, transcriptionText]);

  // Реплика о готовом протоколе: одно замечание по тексту и предложение дать
  // ссылку. Пишется, даже когда панель закрыта, — иначе это та же непрочитанная
  // подсказка, о которую всё и спотыкалось; о непрочитанном сообщает onUnread.
  //
  // Один раз за сеанс работы с протоколом: «Подробнее» и «Короче» рождают новый
  // текст, и повторять реплику на каждое нажатие — навязчивость.
  useEffect(() => {
    if (nudgedRef.current) return;
    const text = protocolReadyMessage({
      protocolText, transcriptText: transcriptionText, canShare,
    });
    if (!text) return;
    nudgedRef.current = true;
    setMessages((prev) => [...prev, { role: 'assistant', content: text, action: 'share' }]);
    if (!visible) onUnread?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [protocolText]);

  if (!visible) return null;

  const handleSend = async () => {
    const text = input.trim();
    if (!text || loading) return;

    setMessages((prev) => [...prev, { role: 'user', content: text }]);
    setInput('');
    setLoading(true);

    try {
      const context = { transcription: chatTranscription, meetingAt };
      if (activeTab !== 'transcription') {
        context.protocol = protocolText;
      }

      if (editMode({ activeTab, protocolText, onProtocolChange, mode })) {
        const { reply, protocol } = await chatService.edit(text, context, model);
        const diff = changedLines(protocolText, protocol);
        setMessages((prev) => [...prev, {
          role: 'assistant', content: reply,
          // Правка ждёт решения только если она что-то меняет.
          ...(diff.length > 0 ? { edit: { diff, protocol, status: 'pending' } } : {}),
        }]);
        return;
      }

      const history = messages.map((m) => ({ role: m.role, content: m.content }));
      const response = await chatService.send(text, context, history, model);
      setMessages((prev) => [...prev, { role: 'assistant', content: response }]);
    } catch (err) {
      // 503 — шлюз перегружен: повтор осмыслен, так и пишем
      const content = describeChatError(err?.response?.status, err?.response?.data?.detail, err?.code);
      // error — чтобы под текстом сбоя не было «Копировать»: копировать там нечего.
      setMessages((prev) => [...prev, { role: 'assistant', content, error: true }]);
    } finally {
      setLoading(false);
    }
  };

  // Новый диалог: чистим переписку и поле ввода, контекст модели обнуляется
  // (history собирается из messages). Авто-приветствие повторно НЕ всплывает
  // — greetedRef уже true, юзер начинает со своего вопроса с чистого листа.
  const handleReset = () => {
    setMessages([]);
    setInput('');
  };

  // «Применить» отдаёт новую версию наверх (тот же путь, что ручная правка —
  // автосохранение в Историю уже висит на нём); «Не надо» оставляет как было.
  // Решение помечается в самой реплике: кнопки исчезают, остаётся подпись.
  const resolveEdit = (index, apply) => {
    setMessages((prev) => prev.map((m, i) => {
      if (i !== index || !m.edit || m.edit.status !== 'pending') return m;
      if (apply) onProtocolChange(m.edit.protocol, 'assistant');
      return { ...m, edit: { ...m.edit, status: apply ? 'applied' : 'rejected' } };
    }));
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    // Оформление по артборду «Бумаги»: карточка колонкой, без рамок и без
    // перетаскивания ширины — в макете у ассистента фиксированные 332px, и
    // ручка ресайза там не нарисована.
    <aside className="bg-paper-card rounded-card flex flex-col min-h-0 overflow-hidden">
      <div className="kbd-hide flex items-center justify-between px-[18px] pt-4 pb-3 shrink-0">
        <span className="text-[14px] font-semibold text-paper-ink">Обсудить с ассистентом</span>
        {messages.length > 0 && (
          <button
            type="button"
            onClick={handleReset}
            title="Очистить контекст и начать новый диалог"
            className={BTN_SECONDARY}
          >
            Новый диалог
          </button>
        )}
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto px-[18px] py-1 flex flex-col gap-[10px]">
        {messages.length === 0 && !loading && (
          <p className="text-[12px] text-paper-ink-3 text-center mt-8">
            Задайте вопрос по {activeTab === 'transcription' ? 'расшифровке' : 'протоколу'}
          </p>
        )}
        {messages.map((msg, i) => (
          <div
            key={i}
            className={`max-w-[92%] rounded-[14px] px-[13px] py-[10px] text-[13px] leading-[1.5]
                        flex flex-col gap-2 ${
              msg.role === 'user'
                ? 'self-end bg-paper-bg text-paper-ink'
                : 'self-start bg-paper-panel text-paper-ink'
            }`}
          >
            {msg.role === 'user' ? msg.content : (
              <div className="prose prose-sm max-w-none prose-p:my-1 prose-ul:my-1 prose-ol:my-1 prose-li:my-0 prose-headings:my-1.5 prose-headings:text-[13px] prose-strong:text-paper-ink prose-code:text-[12px]">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{msg.content}</ReactMarkdown>
              </div>
            )}
            {msg.edit && (
              <EditProposal edit={msg.edit}
                            onApply={() => resolveEdit(i, true)}
                            onReject={() => resolveEdit(i, false)} />
            )}
            {/* Кнопка прямо в реплике: совет без действия рядом с ним — это та
                же подпись под кнопкой, которую не читают. */}
            {msg.action === 'share' && canShare && onShare && (
              <button
                type="button"
                onClick={onShare}
                className="self-start rounded-[8px] bg-paper-accent px-3 py-[6px] text-[12px] font-semibold text-white"
              >
                Дать ссылку
              </button>
            )}
            {msg.role === 'assistant' && !msg.error && !msg.action && <CopyMessageButton text={msg.content} />}
          </div>
        ))}
        {loading && (
          <div className="self-start text-[12px] text-paper-ink-3">Печатает…</div>
        )}
        <div ref={bottomRef} />
      </div>

      <div className="px-[18px] pb-4 pt-2 shrink-0">
        {editAvailable({ activeTab, protocolText, onProtocolChange }) && (
          <div className="kbd-hide mb-2 flex bg-paper-bg rounded-[8px] p-1 gap-1 w-fit" role="radiogroup" aria-label="Режим ассистента">
            {[['ask', 'Вопрос'], ['edit', 'Правка']].map(([key, label]) => (
              <button key={key} type="button" role="radio" aria-checked={mode === key}
                      onClick={() => setMode(key)}
                      className={`rounded-[6px] px-[10px] py-[3px] text-[12px] font-medium transition-colors ${
                        mode === key ? 'bg-paper-card text-paper-ink' : 'text-paper-ink-3'}`}>
                {label}
              </button>
            ))}
          </div>
        )}
        <div className="flex items-center gap-2">
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={mode === 'edit' && editAvailable({ activeTab, protocolText, onProtocolChange })
              ? 'Что поправить в протоколе…' : 'Спросить об этой встрече…'}
            className="flex-1 min-w-0 rounded-[10px] bg-paper-bg px-3 py-2 text-[13px] text-paper-ink placeholder:text-paper-hint outline-none"
          />
          <button
            type="button"
            onClick={handleSend}
            disabled={!input.trim() || loading}
            aria-label="Отправить"
            className="w-9 h-9 shrink-0 rounded-[10px] bg-paper-accent hover:bg-paper-accent-hover disabled:bg-paper-bg disabled:text-paper-hint text-white flex items-center justify-center transition-colors"
          >
            <Send className="w-4 h-4" />
          </button>
        </div>
      </div>
    </aside>
  );
}


/** «Копировать» под ответом ассистента: раньше ответ можно было только выделить
 *  мышью (обращение a8a0258b). В буфер — оформленный текст и простой, как у
 *  «Копировать» протокола (lib/richClipboard.js); до copyRich ничего не await-ить —
 *  Safari пускает в буфер только в такте клика. */
function CopyMessageButton({ text }) {
  const [state, setState] = useState(null); // null | 'done' | 'failed'
  const handleCopy = async () => {
    let next = 'done';
    try {
      const html = renderToStaticMarkup(
        <ReactMarkdown remarkPlugins={[remarkGfm]} disallowedElements={['img']}>{text}</ReactMarkdown>);
      await copyRich(html, htmlToPlain(html));
    } catch {
      next = 'failed';
    }
    setState(next);
    setTimeout(() => setState(null), 1500);
  };
  const label = state === 'done' ? 'Скопировано' : state === 'failed' ? 'Не скопировалось' : 'Копировать';
  return (
    <button type="button" onClick={handleCopy} title={label} aria-label={label}
            className="self-start -ml-1 flex items-center gap-1 rounded-[6px] px-1 py-[2px] text-[11px] text-paper-ink-3 hover:text-paper-ink transition-colors">
      {state === 'done' ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
      {state && <span>{label}</span>}
    </button>
  );
}

/** Diff правки и решение по ней — по артборду «Бумаги» (убранное зачёркнуто,
 *  добавленное подсвечено; «Применить / Не надо»; после решения — подпись). */
function EditProposal({ edit, onApply, onReject }) {
  const shown = edit.diff.slice(0, DIFF_LINES_SHOWN);
  const rest = edit.diff.length - shown.length;
  return (
    <div className="flex flex-col gap-2" data-testid="edit-proposal">
      <div className="rounded-[10px] bg-paper-card px-3 py-2 text-[12px] leading-[1.45] font-mono whitespace-pre-wrap break-words">
        {shown.map((d, n) => (
          <div key={n}
               style={d.k === '-'
                 ? { color: '#8a8a8a', textDecoration: 'line-through' }
                 : { background: 'rgba(229,0,113,.08)' }}>
            {d.t}
          </div>
        ))}
        {rest > 0 && <div className="text-paper-ink-3">… ещё {rest} строк</div>}
      </div>
      {edit.status === 'pending' ? (
        <div className="flex items-center gap-2">
          <button type="button" onClick={onApply}
                  className="rounded-[8px] bg-paper-accent px-3 py-[6px] text-[12px] font-semibold text-white">
            Применить
          </button>
          <button type="button" onClick={onReject}
                  className="rounded-[8px] bg-paper-bg px-3 py-[6px] text-[12px] font-medium text-paper-ink">
            Не надо
          </button>
        </div>
      ) : (
        <div className="text-[12px] text-paper-ink-3">
          {edit.status === 'applied' ? 'Применено · отменить можно над протоколом' : 'Оставили как было'}
        </div>
      )}
    </div>
  );
}
